import { getSettings } from '../../queries.js';
import { PatientTherapySequenceStatus } from '../../database/schema.js';
import { uuidv7 } from 'uuidv7';
import { zql } from '../../schema.js';
import { addDaysUtc, EventLogs } from '../../utils.js';
import { computeTherapyDateFromCycleDay } from '../../util/protocol-cycles.js';
import { createPatch } from 'rfc6902';
import { addEventLog, EventLogResources } from '../util.js';
export async function addInstructionNote(tx, input, userId) {
    if (input.patientProtocolId.length === 0) {
        await addFixedInstructionNote(tx, input, userId);
    }
    else {
        await addProtocolInstructionNote(tx, input, userId);
    }
}
export async function addProtocolInstructionNote(tx, input, userId) {
    if (!Number.isFinite(input.day) || input.day < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    const patientProtocolId = input.patientProtocolId[0];
    if (!patientProtocolId) {
        throw new Error('patientProtocolId is required');
    }
    const patientProtocol = await tx.run(zql.patientProtocols
        .related('protocol')
        .related('patient')
        .where('id', '=', patientProtocolId)
        .where('patientId', '=', input.patientId)
        .where('deletedAt', 'IS', null)
        .one());
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    if (!patientProtocol || !patientProtocol.protocol) {
        throw new Error(`Patient Protocol not found: ${patientProtocolId}`);
    }
    const { daysRepetition } = patientProtocol.protocol;
    const totalCycles = patientProtocol.cycles ?? patientProtocol.protocol.totalCycles;
    const startCycleIndex = (input.cycle ?? 1) - 1;
    const cycleIndices = input.recurringCycle
        ? Array.from({ length: Math.max(0, totalCycles - startCycleIndex) }, (_, i) => startCycleIndex + i)
        : [startCycleIndex];
    const now = Date.now();
    // Past cycles are skipped (recurring notes only fill the remaining ones); today still counts.
    const startOfTodayUtc = new Date().setUTCHours(0, 0, 0, 0);
    const sequenceOps = [];
    const logSequenceOps = [];
    const sequenceGroupId = uuidv7();
    for (const cycleIndex of cycleIndices) {
        // a moved cycle keeps its own start date (CY-378)
        const therapyDate = computeTherapyDateFromCycleDay(patientProtocol, cycleIndex + 1, input.day, daysRepetition, isZeroDayProtocol);
        if (therapyDate < patientProtocol.startDate) {
            throw new Error('Date cannot be before the protocol start date');
        }
        if (therapyDate < startOfTodayUtc)
            continue;
        const newTherapySequenceId = uuidv7();
        const newInstruction = {
            id: newTherapySequenceId,
            patientId: input.patientId,
            sequenceGroupId: sequenceGroupId,
            protocolId: patientProtocol.protocolId,
            patientProtocolId: patientProtocol.id,
            therapyDate,
            prescriptionSource: 'cypro',
            restVialCreated: false,
            cycle: cycleIndex + 1,
            status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
            isNote: input.isNote,
            note: input.note ?? null,
            day: input.day,
            time: input.time,
            createdAt: now,
            createdBy: userId,
        };
        sequenceOps.push(() => tx.mutate.patientTherapySequence.insert(newInstruction));
        if (tx.location === 'server') {
            const patches = createPatch({}, newInstruction);
            logSequenceOps.push(() => addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.patientTherapySequence,
                resourceId: newInstruction.id,
                action: 'create',
                domain: 'prescription',
                event: EventLogs.PATIENT_INSTRUCTION_NOTE_PROTOCOL_ADDED,
                patches,
                performedBy: userId,
                performedAt: now,
                details: {
                    id: newInstruction.id,
                    patientName: patientProtocol?.patient?.fullName ?? '',
                    patientId: patientProtocol?.patient?.id ?? '',
                    protocolName: patientProtocol?.protocol?.name ?? '',
                    cycle: newInstruction?.cycle?.toString() ?? '',
                    day: newInstruction?.day?.toString() ?? '',
                },
            }));
        }
    }
    if (sequenceOps.length === 0) {
        throw new Error('Note date cannot be in the past');
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
}
export async function addFixedInstructionNote(tx, input, userId) {
    if (!input.fixedDate) {
        throw new Error('Either patientProtocolId or fixedDate must be provided');
    }
    const patient = await tx.run(zql.patients.where('id', input.patientId).one());
    const newTherapySequenceId = uuidv7();
    const now = Date.now();
    const instructionNote = {
        id: newTherapySequenceId,
        patientId: input.patientId,
        therapyDate: input.fixedDate,
        sequenceGroupId: uuidv7(),
        prescriptionSource: 'cypro',
        restVialCreated: false,
        status: PatientTherapySequenceStatus.enum.pending_doctor_validation,
        isNote: input.isNote,
        note: input.note ?? null,
        time: input.time,
        createdAt: now,
        createdBy: userId,
    };
    await tx.mutate.patientTherapySequence.insert(instructionNote);
    if (tx.location === 'server') {
        const patches = createPatch({}, instructionNote);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: instructionNote.id,
            action: 'create',
            domain: 'prescription',
            event: EventLogs.PATIENT_INSTRUCTION_NOTE_ADDED,
            patches,
            performedBy: userId,
            performedAt: now,
            status: 'OK',
            details: {
                id: instructionNote.id,
                patientName: patient?.fullName ?? '',
                patientId: patient?.id ?? '',
            },
        });
    }
}
export async function updateInstructionNote(tx, input, userId) {
    const existing = await tx.run(zql.patientTherapySequence.where('id', '=', input.id).where('deletedAt', 'IS', null).one());
    if (!existing)
        throw new Error('Instruction note not found');
    if (existing.patientId !== input.patientId)
        throw new Error('Patient mismatch');
    const isProtocolNote = !!existing.patientProtocolId;
    if (isProtocolNote) {
        await updateProtocolInstructionNote(tx, input, userId);
    }
    else {
        await updateFixedInstructionNote(tx, input, userId);
    }
}
export async function updateProtocolInstructionNote(tx, input, userId) {
    const now = Date.now();
    const prev = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .related('protocol')
        .related('patientProtocol')
        .related('patient')
        .one());
    const settings = await getSettings(tx);
    const isZeroDayProtocol = settings?.dayZeroProtocol;
    if (!prev) {
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    }
    if (!prev.cycle) {
        throw new Error(`Patient Therapy sequence does not belongs to any protocol: ${input.id}`);
    }
    if (input.day === undefined || !Number.isFinite(input.day) || input.day < 0) {
        throw new Error('Day must be provided when patientProtocolId is provided');
    }
    if (!prev.patientProtocol || !prev.protocol) {
        throw new Error(`Patient Protocol not found`);
    }
    const cycle = input.cycle ?? prev.cycle;
    const { daysRepetition } = prev.protocol;
    const therapyDate = computeTherapyDateFromCycleDay(prev.patientProtocol, cycle, input.day, daysRepetition, isZeroDayProtocol);
    if (therapyDate < prev.patientProtocol.startDate) {
        throw new Error('Date cannot be before the protocol start date');
    }
    const startOfTodayUtc = new Date().setUTCHours(0, 0, 0, 0);
    if (therapyDate < startOfTodayUtc) {
        throw new Error('Note date cannot be in the past');
    }
    const sequenceOps = [];
    const logSequenceOps = [];
    // drop the related rows loaded above, they are not columns of patientTherapySequence
    const { medicine: _medicine, protocol: _protocol, patientProtocol: _patientProtocol, patient: _patient, ...prevRow } = prev;
    const updatedInstructionNote = {
        ...prevRow,
        id: input.id,
        patientId: input.patientId,
        protocolId: prev.patientProtocol.protocolId,
        patientProtocolId: prev.patientProtocol.id,
        therapyDate,
        cycle,
        day: input.day,
        ...(input.time !== undefined && { time: input.time }),
        ...(input.note !== undefined && { note: input.note }),
        updatedAt: now,
        updatedBy: userId,
    };
    sequenceOps.push(() => tx.mutate.patientTherapySequence.update(updatedInstructionNote));
    if (tx.location === 'server') {
        const patches = createPatch(prev, updatedInstructionNote);
        logSequenceOps.push(() => addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: updatedInstructionNote.id,
            action: 'create',
            domain: 'prescription',
            event: EventLogs.PATIENT_INSTRUCTION_NOTE_PROTOCOL_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            status: 'OK',
            details: {
                id: updatedInstructionNote.id,
                patientName: prev?.patient?.fullName ?? '',
                patientId: prev?.patient?.id ?? '',
                protocolName: prev?.protocol?.name ?? '',
                cycle: updatedInstructionNote?.cycle?.toString() ?? '',
                day: updatedInstructionNote?.day?.toString() ?? '',
            },
        }));
    }
    if (input.applyToFutureNotes && prev.sequenceGroupId) {
        const siblingSequences = await tx.run(zql.patientTherapySequence
            .where('sequenceGroupId', '=', prev.sequenceGroupId)
            .where('isNote', '=', true)
            .where('deletedAt', 'IS', null));
        if (prev.cycle) {
            for (const seq of siblingSequences) {
                if (seq.id === prev.id || !seq.cycle || seq.cycle < prev.cycle)
                    continue;
                const day = isZeroDayProtocol ? input.day : input.day - 1;
                const siblingCycleOffset = (seq.cycle - 1) * daysRepetition;
                const siblingTherapyDate = addDaysUtc(prev.patientProtocol.startDate, siblingCycleOffset + day);
                if (siblingTherapyDate < startOfTodayUtc)
                    continue;
                const updatedInstruction = {
                    ...seq,
                    id: seq.id,
                    day: input.day,
                    therapyDate: siblingTherapyDate,
                    ...(input.time !== undefined && { time: input.time }),
                    ...(input.note !== undefined && { note: input.note }),
                    updatedAt: now,
                    updatedBy: userId,
                };
                sequenceOps.push(() => tx.mutate.patientTherapySequence.update(updatedInstruction));
                if (tx.location === 'server') {
                    const patches = createPatch(seq, updatedInstruction);
                    logSequenceOps.push(() => addEventLog(tx, {
                        id: uuidv7(),
                        resource: EventLogResources.patientTherapySequence,
                        resourceId: updatedInstruction.id,
                        action: 'create',
                        domain: 'prescription',
                        event: EventLogs.PATIENT_INSTRUCTION_NOTE_PROTOCOL_UPDATED,
                        patches,
                        performedBy: userId,
                        performedAt: now,
                        status: 'OK',
                        details: {
                            id: updatedInstruction.id,
                            patientName: prev?.patient?.fullName ?? '',
                            patientId: prev?.patient?.id ?? '',
                            protocolName: prev?.protocol?.name ?? '',
                            cycle: updatedInstruction?.cycle?.toString() ?? '',
                            day: updatedInstruction?.day?.toString() ?? '',
                        },
                    }));
                }
            }
        }
    }
    await Promise.all(sequenceOps.map(op => op()));
    await Promise.all(logSequenceOps.map(op => op()));
}
export async function updateFixedInstructionNote(tx, input, userId) {
    const now = Date.now();
    const prev = await tx.run(zql.patientTherapySequence
        .where('id', '=', input.id)
        .related('medicine', q => q.where('deletedAt', 'IS', null))
        .related('protocol')
        .related('patientProtocol')
        .related('patient')
        .one());
    if (!prev)
        throw new Error(`Patient Therapy sequence not found: ${input.id}`);
    // drop the related rows loaded above, they are not columns of patientTherapySequence
    const { medicine: _medicine, protocol: _protocol, patientProtocol: _patientProtocol, patient: _patient, ...prevRow } = prev;
    const newInstruction = {
        ...prevRow,
        id: input.id,
        ...(input.fixedDate !== undefined && input.fixedDate !== null && { therapyDate: input.fixedDate }),
        ...(input.time !== undefined && { time: input.time }),
        ...(input.note !== undefined && { note: input.note }),
        updatedAt: now,
        updatedBy: userId,
    };
    await tx.mutate.patientTherapySequence.update(newInstruction);
    if (tx.location === 'server') {
        const patches = createPatch(prev, newInstruction);
        await addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.patientTherapySequence,
            resourceId: newInstruction.id,
            action: 'update',
            domain: 'prescription',
            event: EventLogs.PATIENT_INSTRUCTION_NOTE_UPDATED,
            patches,
            performedBy: userId,
            performedAt: now,
            status: 'OK',
            details: {
                id: newInstruction.id,
                patientName: prev?.patient?.fullName ?? '',
                patientId: prev?.patient?.id ?? '',
            },
        });
    }
}
//# sourceMappingURL=patient-instruction-notes.js.map